import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Popup, CircleMarker } from 'react-leaflet';
import { getTileLayerConfig } from '../config/mapTiles';
import {
    MARKER_COLORS,
    buildMapData,
    getMarkerColor,
    getMarkerSize,
    summarizeMapData,
} from '../utils/geoMapData';

// Tile options are resolved once from the build-time environment
const tileLayerConfig = getTileLayerConfig();

/** Legend entry: colored dot plus label */
const LegendItem = ({ color, label, className }) => (
    <div className={`d-flex align-items-center ${className || ''}`}>
        <div
            style={{
                width: '12px',
                height: '12px',
                backgroundColor: color,
                borderRadius: '50%',
                marginRight: '8px'
            }}
        ></div>
        <small>{label}</small>
    </div>
);

const GeographicMap = ({ countries = [], locations = [] }) => {
    // Geolocate entities and merge duplicates (pure, synchronous computation)
    const mapData = useMemo(() => buildMapData(countries, locations), [countries, locations]);
    const summary = summarizeMapData(mapData);

    if (mapData.length === 0) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <i className="fas fa-globe fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Geographic Data</h5>
                    <p className="text-muted">
                        No countries or locations were found in the document that can be mapped.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <h6 className="mb-0">
                    <i className="fas fa-map-marked-alt me-2"></i>
                    Geographic Distribution
                    <span className="badge bg-primary ms-2">{mapData.length} locations</span>
                </h6>
            </div>
            <div className="card-body p-0">
                <div className="map-container">
                    <MapContainer
                        center={[20, 0]}
                        zoom={2}
                        style={{ height: '400px', width: '100%' }}
                        scrollWheelZoom={true}
                    >
                        <TileLayer {...tileLayerConfig} />
                        
                        {mapData.map(item => (
                            <CircleMarker
                                key={item.id}
                                center={item.coordinates}
                                radius={getMarkerSize(item.count)}
                                fillColor={getMarkerColor(item.type)}
                                color="#fff"
                                weight={2}
                                opacity={1}
                                fillOpacity={0.7}
                            >
                                <Popup>
                                    <div>
                                        <strong>{item.name}</strong>
                                        <br />
                                        <small className="text-muted">
                                            Type: {item.type}
                                            {item.count > 1 && (
                                                <>
                                                    <br />
                                                    Mentions: {item.count}
                                                </>
                                            )}
                                        </small>
                                    </div>
                                </Popup>
                            </CircleMarker>
                        ))}
                    </MapContainer>
                    
                    {/* Legend */}
                    <div className="map-legend">
                        <div className="mb-2">
                            <strong>Legend</strong>
                        </div>
                        <LegendItem color={MARKER_COLORS.country} label="Countries" className="mb-1" />
                        <LegendItem color={MARKER_COLORS.location} label="Locations" />
                    </div>
                </div>
            </div>
            
            <div className="card-footer">
                <div className="row">
                    <div className="col-md-6">
                        <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            Circle size indicates mention frequency
                        </small>
                    </div>
                    <div className="col-md-6 text-end">
                        <small className="text-muted">
                            Countries: {summary.countries} | Locations: {summary.locations}
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeographicMap;
